/* eslint-disable max-len */
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import styles from "./Modal.module.scss";

interface ModalProps {
  title: string;
  onClose: (outsideClick: boolean) => void;
  options?: ModalOptions;
  children?: ReactNode | undefined;
}

export interface GenericModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

export const ModalComponent: React.FC<ModalProps> = ({
  onClose,
  options,
  children,
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [w, setWidth] = useState(0);
  const [h, setHeight] = useState(0);

  // This function calculates width and height of the list
  const getContainerSize = (): void => {
    if (contentRef.current) {
      if (contentRef.current.clientWidth) {
        const newWidth = contentRef.current.clientWidth || 0;
        setWidth(newWidth);
      }
      if (contentRef.current.clientHeight) {
        const newHeight = contentRef.current.clientHeight || 0;
        setHeight(newHeight);
      }
    }
  };

  // Get 'width' and 'height' after the initial render and every time the list changes
  useEffect(() => {
    getContainerSize();
  }, [contentRef]);

  const onDocumentClick = useCallback(
    (event: Event) => {
      if (
        contentRef.current &&
        event.target &&
        event.target instanceof Element &&
        !contentRef.current.contains(event.target) &&
        !options?.persistent
      ) {
        onClose(true);
      }
    },
    [contentRef, onClose, options]
  );

  useEffect(() => {
    setTimeout(() => {
      document.addEventListener("click", onDocumentClick);
    });

    return (): void => {
      document.removeEventListener("click", onDocumentClick);
    };
  }, [onDocumentClick, onClose]);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!options?.persistent) {
      if (event.key === "Escape") onClose(false);
    }
  };

  useEffect((): (() => void) => {
    document.addEventListener("keydown", onKeyDown);
    return (): void => document.removeEventListener("keydown", onKeyDown);
  });

  const background = `M21.401 267.667
    V42.7552
    L61.5052 2.66667
    H161.604
    L173.443 14.4896
    H${h - (360 - 349.745)}
    L${w - (648 - 361.573)} 2.66667
    H${w - (648 - 568.745)}
    L${w - (648 - 644.437)} 78.3594
    V${h - (360 - 329.396)}
    H83.1406
    L21.401 ${h - (360 - 267.667)}
    Z`;

  const mainFrame = `M21.401 267.667
    V42.7552
    L61.5052 2.66667
    H161.604
    L173.443 14.4896
    H349.745
    L361.573 2.66667
    H${w - (648 - 568.745)}
    L${w - (648 - 644.437)} 78.3594
    V${h - (360 - 329.396)}
    H83.1406
    L21.401 ${h - (360 - 267.667)}
    Z
    M${w - (648 - 629.172)} 59.3177
    V43.7865
    L${w - (648 - 600.182)} 14.7865
    H${w - (648 - 584.667)}
    L${w - (648 - 569.865)} 0
    H60.3906
    L18.7344 41.651
    V107.036
    L0 125.771
    V${h - (360 - 250.01)}
    L91.7917 ${h - (360 - 341.823)}
    H238.453
    L258.01 ${h - (360 - 361.385)}
    H${w - (648 - 532.344)}
    L${w - (648 - 551.906)} ${h - (360 - 341.823)}
    H${w - (648 - 636.932)}
    L${w - (648 - 646.693)} ${h - (360 - 332.062)}
    H${w - (648 - 647.104)}
    V77.25
    L${w - (648 - 629.172)} 59.3177
    Z`;

  return ReactDOM.createPortal(
    <div className={styles["modal"]}>
      <div className={styles["modal-background"]}>
        <svg viewBox={`0 0 ${w} ${h}`} fill="none">
          <linearGradient
            id="grad1"
            x1="0.952"
            y1="0.5"
            x2="0.056"
            y2="0.563"
            gradientUnits="objectBoundingBox">
            <stop offset="0" stopColor="#67747c11" />
            <stop offset="1" stopColor="#57697111" />
          </linearGradient>
          <linearGradient
            id="grad3"
            x1="0.952"
            y1="0.5"
            x2="0.056"
            y2="0.563"
            gradientUnits="objectBoundingBox">
            <stop offset="0" stopColor="#becfda" />
            <stop offset="1" stopColor="#a5b2b9" />
          </linearGradient>
          <linearGradient
            id="grad5"
            x1="0.952"
            y1="0.5"
            x2="0.056"
            y2="0.563"
            gradientUnits="objectBoundingBox">
            <stop offset="0" stopColor="#67747c" />
            <stop offset="1" stopColor="#576971" />
          </linearGradient>
          <path d={mainFrame} fill="url(#grad5)" />
          <path d={background} fill="url(#grad1)" />
          <g
            onClick={(): void => onClose(false)}
            fill="url(#grad3)"
            transform={`translate(620 3) scale(0.45 0.45)`}>
            <path
              id="button-close-border"
              strokeWidth="3"
              stroke="#ffffff00"
              d="M35.1574 18.2262L27.7304 25.6499L35.1504 33.0734L33.4235 34.8002L26.0018 27.375L18.5764 34.797L16.85 33.0706L24.2771 25.6469L16.8571 18.2234L18.5839 16.4966L26.0057 23.9218L33.431 16.4998L35.1574 18.2262ZM26.0069 0.828333C19.2818 0.83092 12.9551 3.41133 8.19599 8.09451C3.43685 12.7777 0.80815 19.018 0.807302 25.6516C0.805552 39.3368 12.1065 50.4685 26.0006 50.4685C33.0175 50.4676 39.3718 47.6282 43.9462 43.0538C48.4291 38.5709 51.1993 32.4204 51.2002 25.6481C51.2019 11.96 39.901 0.828283 26.0069 0.828333Z"
            />
          </g>
        </svg>
      </div>

      <div className={styles["modal-content"]} ref={contentRef}>
        <div className={styles["modal-body"]}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export type ModalOptions = Partial<{
  persistent: boolean;
  onClose: () => void;
  onSuccess: () => void;
  disableOutsideClick: boolean;
}>;

interface ModalContextValue {
  registerModal: (
    uuid: string,
    node: React.ReactNode,
    title: string,
    options?: ModalOptions
  ) => void;
  showModal: (
    uuid: string,
    node: React.ReactNode,
    title: string,
    options?: ModalOptions
  ) => void;
  closeModal: (uuid: string) => void;
  isOpen: (uuid: string) => boolean;
}

interface ModalConfig {
  node: React.ReactNode;
  title: string;
  options?: Partial<ModalOptions>;
  isOpen?: boolean;
}

const ModalContext = React.createContext<ModalContextValue>({
  registerModal: () => {
    void 0;
  },
  closeModal: () => {
    void 0;
  },
  isOpen: () => false,
  showModal: () => {
    void 0;
  },
});

type ModalProviderProps = {
  children?: React.ReactNode | undefined;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = (
  props: ModalProviderProps
) => {
  const [modalList, setModalList] = useState<{ [key: string]: ModalConfig }>(
    {}
  );

  const registerModal = (
    uuid: string,
    node: ReactNode,
    title: string,
    options?: ModalOptions
  ): void => {
    if (!modalList[uuid]) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { isOpen: false, node, options: options || {}, title },
      }));
    }
  };

  const showModal = (
    uuid: string,
    node: ReactNode,
    title: string,
    options?: ModalOptions
  ): void => {
    if (!modalList[uuid]) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { isOpen: false, node, options: options || {}, title },
      }));
    }

    if (!modalList[uuid]?.isOpen) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { ...modals[uuid]!, isOpen: true },
      }));
    }
  };

  const closeModal = (uuid: string, callOnClose = true): void => {
    const modal = modalList[uuid];
    if (modal) {
      setModalList((modals) => ({
        ...modals,
        [uuid]: { ...modals[uuid]!, isOpen: false },
      }));
      if (modal.options?.onClose && callOnClose) {
        modal.options.onClose();
      }
    }
  };

  const isOpen = (uuid: string): boolean => {
    return (modalList[uuid] !== undefined && modalList[uuid]?.isOpen) || false;
  };

  const onSuccess = (uuid: string): void => {
    const modal = modalList[uuid];
    closeModal(uuid, false);
    if (modal && modal.isOpen && modal.options?.onSuccess) {
      modal.options.onSuccess();
    }
  };

  return (
    <ModalContext.Provider
      value={{
        closeModal,
        isOpen,
        showModal,
        registerModal,
      }}>
      {props.children}
      {Object.keys(modalList)
        .filter((uuid) => modalList[uuid]?.isOpen)
        .map((uuid) => {
          const node = modalList[uuid]!.node;

          return (
            <ModalComponent
              key={`modal-${uuid}`}
              options={modalList[uuid]!.options}
              onClose={(outsideClick): void => {
                if (
                  modalList[uuid]?.options?.disableOutsideClick &&
                  outsideClick
                ) {
                  return;
                }
                closeModal(uuid);
              }}
              title={modalList[uuid]!.title}>
              {React.isValidElement(node) &&
                React.cloneElement(node as React.ReactElement<ModalOptions>, {
                  onClose: () => {
                    closeModal(uuid);
                  },
                  onSuccess: () => {
                    onSuccess(uuid);
                  },
                })}
            </ModalComponent>
          );
        })}
    </ModalContext.Provider>
  );
};

export interface ModalHook {
  showModal: () => void;
  closeModal: () => void;
  isOpen: boolean;
}

export const useModal = (
  title: string,
  node: ReactNode,
  options?: ModalOptions
): ModalHook => {
  const { registerModal, showModal, closeModal, isOpen } =
    useContext(ModalContext);
  const ref = useRef<ReactNode>(node);

  useEffect(() => {
    registerModal(title, node, title, options);
  }, [title, registerModal, node, options]);

  useEffect(() => {
    ref.current = node;
  }, [node]);

  const presentModal = useCallback(() => {
    showModal(title, ref.current, title, options);
  }, [title, ref, showModal, options]);

  const unpresentModal = useCallback(() => {
    closeModal(title);
  }, [closeModal, title]);

  const isModalOpen = useMemo(() => isOpen(title), [isOpen, title]);

  return {
    closeModal: unpresentModal,
    isOpen: isModalOpen,
    showModal: presentModal,
  };
};
